/**
 * Collection page (new) — progressive enhancement.
 * Uses Section Rendering API to refresh the section without PJAX/jQuery.
 *
 * Draft themes: test with ?view=collection-new (+ preview_theme_id).
 * After the theme is published and collections are assigned collection-new,
 * you can remove view / preview_theme_id preservation in this file and
 * snippets/collection-view-param.liquid.
 *
 * Mobile sheet lives outside [data-ts-collection-page] so AJAX replace
 * does not unmount the open drawer.
 */
(function () {
  'use strict';

  var globalBound = false;

  function qs(root, sel) {
    return root ? root.querySelector(sel) : null;
  }

  function qsa(root, sel) {
    return root ? Array.prototype.slice.call(root.querySelectorAll(sel)) : [];
  }

  function lockScroll(locked) {
    document.documentElement.classList.toggle('ts-scroll-locked', locked);
    document.body.style.overflow = locked ? 'hidden' : '';
  }

  /**
   * Keep draft-preview params across filter / sort / pagination.
   * Remove after collections use assigned collection-new (no ?view=).
   */
  function preservePreviewParams(url) {
    var current = new URL(window.location.href);
    var view = current.searchParams.get('view');
    var previewThemeId = current.searchParams.get('preview_theme_id');
    if (view) url.searchParams.set('view', view);
    if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
    return url;
  }

  /**
   * Resolve section HTML from Section Rendering JSON.
   * Supports { sections: { id: html } } and top-level { id: html }.
   * With ?view=, response keys may not match data-section-id.
   * Returns { html, sectionId } or null.
   */
  function getSectionHtml(data, preferredId) {
    if (!data || typeof data !== 'object') return null;

    var bag = data.sections && typeof data.sections === 'object' ? data.sections : data;
    if (!bag || typeof bag !== 'object') return null;

    function isHtml(value) {
      return typeof value === 'string' && value.indexOf('<') !== -1;
    }

    if (preferredId && isHtml(bag[preferredId])) {
      return { html: bag[preferredId], sectionId: preferredId };
    }

    var keys = Object.keys(bag).filter(function (key) {
      return isHtml(bag[key]);
    });

    if (keys.length === 1) {
      return { html: bag[keys[0]], sectionId: keys[0] };
    }

    var i;
    for (i = 0; i < keys.length; i++) {
      if (bag[keys[i]].indexOf('data-ts-collection-page') !== -1) {
        return { html: bag[keys[i]], sectionId: keys[i] };
      }
    }

    return null;
  }

  function formatPriceLabel(label, symbol, min, max) {
    return label + ': ' + symbol + min + '-' + symbol + max;
  }

  var PRICE_THUMB_SIZE = 20;

  function valueToOffset(value, bound, width) {
    if (bound <= 0 || width <= 0) return PRICE_THUMB_SIZE / 2;
    var usable = Math.max(width - PRICE_THUMB_SIZE, 0);
    return PRICE_THUMB_SIZE / 2 + (value / bound) * usable;
  }

  /** Recalculate knob/range positions (safe to call when sheet becomes visible). */
  function syncPriceSlider(wrap) {
    if (!wrap) return;
    var minInput = qs(wrap, '[data-ts-price-min]');
    var maxInput = qs(wrap, '[data-ts-price-max]');
    var rangeEl = qs(wrap, '[data-ts-price-range]');
    var summaryEl = qs(wrap, '[data-ts-price-summary]');
    var knobMin = qs(wrap, '[data-ts-price-knob-min]');
    var knobMax = qs(wrap, '[data-ts-price-knob-max]');
    var slider = qs(wrap, '.ts-collection-facet__price-slider') || wrap;
    var symbol = wrap.getAttribute('data-currency-symbol') || '$';
    var label = wrap.getAttribute('data-price-label') || 'Price';

    if (!minInput || !maxInput) return;

    var min = Number(minInput.value);
    var max = Number(maxInput.value);
    var bound = Number(minInput.max) || 0;
    var width = slider.offsetWidth || minInput.offsetWidth || 0;

    if (max < min) {
      if (document.activeElement === minInput) {
        maxInput.value = String(min);
        max = min;
      } else {
        minInput.value = String(max);
        min = max;
      }
    }

    var leftPx = valueToOffset(min, bound, width);
    var rightPx = valueToOffset(max, bound, width);

    if (rangeEl) {
      rangeEl.style.left = leftPx + 'px';
      rangeEl.style.width = Math.max(rightPx - leftPx, 0) + 'px';
      rangeEl.style.right = 'auto';
    }

    if (knobMin) knobMin.style.left = leftPx + 'px';
    if (knobMax) knobMax.style.left = rightPx + 'px';

    if (summaryEl) {
      summaryEl.textContent = formatPriceLabel(label, symbol, minInput.value, maxInput.value);
    }

    minInput.style.zIndex = min > bound - bound * 0.05 ? '5' : '3';
    maxInput.style.zIndex = '4';
    if (knobMin) knobMin.style.zIndex = minInput.style.zIndex;
    if (knobMax) knobMax.style.zIndex = maxInput.style.zIndex;
  }

  function syncPriceSliders(root) {
    qsa(root, '[data-ts-price-slider]').forEach(syncPriceSlider);
  }

  function initPriceSliders(root) {
    qsa(root, '[data-ts-price-slider]').forEach(function (wrap) {
      if (wrap.dataset.tsPriceBound === 'true') return;
      wrap.dataset.tsPriceBound = 'true';

      var minInput = qs(wrap, '[data-ts-price-min]');
      var maxInput = qs(wrap, '[data-ts-price-max]');
      if (!minInput || !maxInput) return;

      function onSync() {
        syncPriceSlider(wrap);
      }

      minInput.addEventListener('input', onSync);
      maxInput.addEventListener('input', onSync);
      window.addEventListener('resize', onSync);
      onSync();
    });
  }

  function CollectionPage(sectionEl) {
    this.sectionEl = sectionEl;
    this.sectionId = sectionEl.getAttribute('data-section-id');
    this.sheet = document.querySelector('[data-ts-collection-sheet]');
    this.lastFocus = null;
    this.pending = false;
    this.syncRefs();
    this.bind();
  }

  CollectionPage.prototype.syncRefs = function () {
    var forms = qsa(this.sectionEl, '[data-ts-facets-form]');
    if (this.sheet) {
      forms = forms.concat(qsa(this.sheet, '[data-ts-facets-form]'));
    }
    this.forms = forms;
    this.form = forms[0] || null;
    this.sort = qs(this.sectionEl, '[data-ts-sort]');
  };

  CollectionPage.prototype.setLoading = function (loading) {
    var wrap = this.sectionEl.closest('.section-collection-wrap') || this.sectionEl;
    wrap.classList.toggle('is-loading', loading);
  };

  CollectionPage.prototype.bindForm = function (form) {
    var self = this;
    if (!form || form.dataset.tsFacetsBound === 'true') return;
    form.dataset.tsFacetsBound = 'true';

    form.addEventListener('change', function (event) {
      if (!event.target) return;
      if (event.target.matches('[data-ts-sort]')) return;
      self.submitForm({ resetPage: true, form: form });
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      self.submitForm({ resetPage: true, form: form });
    });
  };

  CollectionPage.prototype.bind = function () {
    var self = this;

    if (this.sectionEl.dataset.tsCollectionBound === 'true') return;
    this.sectionEl.dataset.tsCollectionBound = 'true';

    initPriceSliders(this.sectionEl);
    qsa(this.sectionEl, '[data-ts-facets-form]').forEach(function (form) {
      self.bindForm(form);
    });

    if (this.sort && this.sort.dataset.tsSortBound !== 'true') {
      this.sort.dataset.tsSortBound = 'true';
      this.sort.addEventListener('change', function () {
        self.forms.forEach(function (form) {
          var input = form.querySelector('input[name="sort_by"], select[name="sort_by"]');
          if (input) input.value = self.sort.value;
        });
        self.submitForm({ resetPage: true, form: self.forms[0] });
      });
    }

    this.bindSortMenu();

    this.sectionEl.addEventListener('click', function (event) {
      var link =
        event.target &&
        event.target.closest &&
        event.target.closest(
          '.ts-collection-pagination a, .ts-collection-active a, .ts-collection-facets__clear, .ts-collection-facets__reset, .ts-collection-empty a'
        );
      if (link && link.getAttribute('href')) {
        event.preventDefault();
        self.refreshByUrl(link.getAttribute('href'));
        return;
      }

      var open = event.target && event.target.closest && event.target.closest('[data-ts-open-filters]');
      if (open) {
        event.preventDefault();
        self.openSheet(open);
      }
    });

    if (this.sheet && this.sheet.dataset.tsSheetBound !== 'true') {
      this.sheet.dataset.tsSheetBound = 'true';
      initPriceSliders(this.sheet);
      qsa(this.sheet, '[data-ts-facets-form]').forEach(function (form) {
        self.bindForm(form);
      });

      this.sheet.addEventListener('click', function (event) {
        var close = event.target && event.target.closest && event.target.closest('[data-ts-sheet-close]');
        if (close) {
          event.preventDefault();
          self.closeSheet();
          return;
        }

        var clear =
          event.target &&
          event.target.closest &&
          event.target.closest('.ts-collection-facets__clear, .ts-collection-facets__reset');
        if (clear && clear.getAttribute('href')) {
          event.preventDefault();
          self.refreshByUrl(clear.getAttribute('href'));
        }
      });
    }

    if (!globalBound) {
      globalBound = true;
      document.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') return;
        var sheet = document.querySelector('[data-ts-collection-sheet]');
        if (sheet && !sheet.hidden) {
          self.closeSheet();
          return;
        }
        closeOpenSortMenu();
      });

      document.addEventListener('click', function (event) {
        var openMenu = document.querySelector('[data-ts-sort-menu]:not([hidden])');
        if (!openMenu) return;
        var wrap = openMenu.closest('[data-ts-sort-wrap]');
        if (wrap && wrap.contains(event.target)) return;
        closeOpenSortMenu();
      });

      window.addEventListener('popstate', function () {
        self.refreshByUrl(window.location.href, { replaceState: true });
      });
    }
  };

  function setSortExpanded(trigger, menu, open) {
    if (!trigger || !menu) return;
    menu.hidden = !open;
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    var down = qs(trigger, '[data-ts-sort-chevron-down]');
    var up = qs(trigger, '[data-ts-sort-chevron-up]');
    if (down) down.hidden = !!open;
    if (up) up.hidden = !open;
  }

  function closeOpenSortMenu() {
    var openMenu = document.querySelector('[data-ts-sort-menu]:not([hidden])');
    if (!openMenu) return;
    var wrap = openMenu.closest('[data-ts-sort-wrap]');
    var trigger = wrap && qs(wrap, '[data-ts-sort-trigger]');
    setSortExpanded(trigger, openMenu, false);
  }

  CollectionPage.prototype.bindSortMenu = function () {
    var self = this;
    var wrap = qs(this.sectionEl, '[data-ts-sort-wrap]');
    if (!wrap || wrap.dataset.tsSortMenuBound === 'true') return;
    wrap.dataset.tsSortMenuBound = 'true';

    var trigger = qs(wrap, '[data-ts-sort-trigger]');
    var menu = qs(wrap, '[data-ts-sort-menu]');
    if (!trigger || !menu) return;

    trigger.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      setSortExpanded(trigger, menu, menu.hidden);
    });

    menu.addEventListener('click', function (event) {
      var option =
        event.target && event.target.closest && event.target.closest('[role="option"]');
      if (!option || !menu.contains(option)) return;
      event.preventDefault();

      var value = option.getAttribute('data-value');
      if (!self.sort || value == null) return;

      self.sort.value = value;
      qsa(menu, '[role="option"]').forEach(function (el) {
        el.setAttribute('aria-selected', el === option ? 'true' : 'false');
      });
      var label = qs(wrap, '[data-ts-sort-label]');
      if (label) label.textContent = option.textContent.trim();

      setSortExpanded(trigger, menu, false);
      self.sort.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  CollectionPage.prototype.openSheet = function (trigger) {
    if (!this.sheet) this.sheet = document.querySelector('[data-ts-collection-sheet]');
    if (!this.sheet) return;
    this.lastFocus = trigger || document.activeElement;
    this.sheet.hidden = false;
    lockScroll(true);

    // Sheet was hidden during init (width=0) — resync knobs after layout.
    var sheet = this.sheet;
    requestAnimationFrame(function () {
      syncPriceSliders(sheet);
    });

    var focusable = qsa(this.sheet, 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])').filter(
      function (el) {
        return !el.hasAttribute('disabled');
      }
    );
    if (focusable[0]) focusable[0].focus();
  };

  CollectionPage.prototype.closeSheet = function () {
    if (!this.sheet) this.sheet = document.querySelector('[data-ts-collection-sheet]');
    if (!this.sheet) return;
    this.sheet.hidden = true;
    lockScroll(false);
    if (this.lastFocus && this.lastFocus.focus) {
      this.lastFocus.focus();
    }
  };

  CollectionPage.prototype.submitForm = function (opts) {
    var form = (opts && opts.form) || this.form;
    if (!form) return;
    var resetPage = opts && opts.resetPage;

    var url = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
    var data = new FormData(form);

    if (!data.has('sort_by') && this.sort) data.set('sort_by', this.sort.value);
    if (resetPage) data.delete('page');

    // Do not send full-range price — Shopify treats it as an active price filter.
    qsa(form, '[data-ts-price-slider]').forEach(function (wrap) {
      var minInput = qs(wrap, '[data-ts-price-min]');
      var maxInput = qs(wrap, '[data-ts-price-max]');
      if (!minInput || !maxInput) return;

      var min = Number(minInput.value);
      var max = Number(maxInput.value);
      var bound = Number(minInput.max);
      if (!(bound > 0)) return;

      if (min <= 0.001 && Math.abs(max - bound) <= 0.001) {
        data.delete(minInput.name);
        data.delete(maxInput.name);
      }
    });

    data.forEach(function (value, key) {
      url.searchParams.append(key, value);
    });

    preservePreviewParams(url);
    this.refreshByUrl(url.toString());
  };

  CollectionPage.prototype.refreshByUrl = function (url, opts) {
    var self = this;
    if (this.pending) return;
    this.pending = true;
    this.setLoading(true);

    var target = preservePreviewParams(new URL(url, window.location.origin));
    var nextUrl = preservePreviewParams(new URL(target.toString()));
    nextUrl.searchParams.delete('sections');
    nextUrl.searchParams.delete('section_id');

    if (this.sectionId) {
      target.searchParams.set('sections', this.sectionId);
    }

    fetch(target.toString(), { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Collection section refresh failed');
        return res.json();
      })
      .then(function (data) {
        var resolved = getSectionHtml(data, self.sectionId);
        if (!resolved || !resolved.html) throw new Error('Missing section HTML');

        if (resolved.sectionId && resolved.sectionId !== self.sectionId) {
          self.sectionId = resolved.sectionId;
        }

        self.replaceSection(resolved.html);

        if (!(opts && opts.replaceState)) {
          window.history.pushState({}, '', nextUrl.pathname + nextUrl.search + nextUrl.hash);
        }
        // Keep sheet open — it lives outside the replaced page root.
      })
      .catch(function (err) {
        console.error('[collection-page]', err);
        window.location.href = nextUrl.toString();
      })
      .finally(function () {
        self.pending = false;
        self.setLoading(false);
      });
  };

  CollectionPage.prototype.replaceSection = function (html) {
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');
    var next = qs(doc, '[data-ts-collection-page]');
    if (!next) {
      throw new Error('Missing [data-ts-collection-page] in section HTML');
    }
    this.sectionEl.replaceWith(next);
    this.sectionEl = next;

    var idFromDom = this.sectionEl.getAttribute('data-section-id');
    if (idFromDom) this.sectionId = idFromDom;

    this.sheet = document.querySelector('[data-ts-collection-sheet]');

    // Sync drawer facets from rendered HTML without unmounting the open sheet shell
    if (this.sheet) {
      var nextFacets = qs(doc, '[data-ts-collection-sheet] .ts-collection-sheet__facets');
      var liveFacets = qs(this.sheet, '.ts-collection-sheet__facets');
      if (nextFacets && liveFacets) {
        liveFacets.innerHTML = nextFacets.innerHTML;
        qsa(liveFacets, '[data-ts-facets-form]').forEach(function (form) {
          form.dataset.tsFacetsBound = '';
        });
        initPriceSliders(liveFacets);
        var self = this;
        qsa(liveFacets, '[data-ts-facets-form]').forEach(function (form) {
          self.bindForm(form);
        });
      }
    }

    this.syncRefs();
    this.bind();
  };

  function init() {
    qsa(document, '[data-ts-collection-page]').forEach(function (el) {
      new CollectionPage(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
